/**
 * Waste Module Constants
 *
 * @module core/modules/waste/constants
 *
 * @description
 * Constantes para el módulo de gestión de residuos.
 * NO incluye constantes de modos - esos se gestionan globalmente.
 */

import type {
  VehicleType,
  VehicleStatus,
  ContainerType,
  ContainerStatus,
  DepotType,
  DepotStatus,
  CollectionFrequency,
  DayOfWeek,
  DriverStatus,
  CrewStatus,
  CrewShift,
  LicenseType,
} from '../types';

// ============================================================================
// VEHICLE CONSTANTS
// ============================================================================

export const VEHICLE_TYPES: { value: VehicleType; label: string; icon: string }[] = [
  { value: 'compactor', label: 'Compactador', icon: 'truck' },
  { value: 'side_loader', label: 'Carga lateral', icon: 'truck-loading' },
  { value: 'rear_loader', label: 'Carga trasera', icon: 'truck-pickup' },
  { value: 'container_truck', label: 'Porta contenedor', icon: 'container' },
  { value: 'pickup', label: 'Camioneta', icon: 'pickup' },
  { value: 'sweeper', label: 'Barredora', icon: 'sweeper' },
];

export const VEHICLE_STATUSES: { value: VehicleStatus; label: string; color: string }[] = [
  { value: 'active', label: 'Activo', color: '#22c55e' },
  { value: 'inactive', label: 'Inactivo', color: '#6b7280' },
  { value: 'maintenance', label: 'Mantenimiento', color: '#f59e0b' },
  { value: 'on_route', label: 'En ruta', color: '#3b82f6' },
];

// ============================================================================
// CONTAINER CONSTANTS
// ============================================================================

export const CONTAINER_TYPES: { value: ContainerType; label: string; color: string }[] = [
  { value: 'general', label: 'Residuos generales', color: '#6b7280' },
  { value: 'recyclable', label: 'Reciclables', color: '#3b82f6' },
  { value: 'organic', label: 'Orgánicos', color: '#22c55e' },
  { value: 'glass', label: 'Vidrio', color: '#06b6d4' },
  { value: 'paper', label: 'Papel/Cartón', color: '#a855f7' },
  { value: 'plastic', label: 'Plástico', color: '#eab308' },
  { value: 'hazardous', label: 'Peligrosos', color: '#ef4444' },
  { value: 'electronic', label: 'Electrónicos', color: '#ec4899' },
];

export const CONTAINER_STATUSES: { value: ContainerStatus; label: string; color: string }[] = [
  { value: 'active', label: 'Activo', color: '#22c55e' },
  { value: 'full', label: 'Lleno', color: '#ef4444' },
  { value: 'damaged', label: 'Dañado', color: '#f59e0b' },
  { value: 'removed', label: 'Removido', color: '#6b7280' },
];

// ============================================================================
// DEPOT CONSTANTS
// ============================================================================

export const DEPOT_TYPES: { value: DepotType; label: string; icon: string; color: string }[] = [
  { value: 'garage', label: 'Cochera/Guarda', icon: 'warehouse', color: '#6366f1' },
  { value: 'transfer_station', label: 'Estación de transferencia', icon: 'arrow-right-left', color: '#8b5cf6' },
  { value: 'landfill', label: 'Relleno sanitario', icon: 'mountain', color: '#78716c' },
  { value: 'recycling_center', label: 'Centro de reciclaje', icon: 'recycle', color: '#22c55e' },
  { value: 'composting', label: 'Planta de compostaje', icon: 'leaf', color: '#84cc16' },
  { value: 'hazardous_waste', label: 'Residuos peligrosos', icon: 'alert-triangle', color: '#ef4444' },
  { value: 'collection_point', label: 'Punto verde', icon: 'map-pin', color: '#14b8a6' },
  { value: 'other', label: 'Otro', icon: 'building', color: '#94a3b8' },
];

export const DEPOT_STATUSES: { value: DepotStatus; label: string; color: string }[] = [
  { value: 'active', label: 'Activo', color: '#22c55e' },
  { value: 'inactive', label: 'Inactivo', color: '#6b7280' },
  { value: 'maintenance', label: 'Mantenimiento', color: '#f59e0b' },
];

// ============================================================================
// FREQUENCY & SCHEDULE CONSTANTS
// ============================================================================

export const COLLECTION_FREQUENCIES: { value: CollectionFrequency; label: string }[] = [
  { value: 'daily', label: 'Diaria' },
  { value: 'weekly_mwf', label: 'Lun-Mié-Vie' },
  { value: 'weekly_tts', label: 'Mar-Jue-Sáb' },
  { value: 'weekly_once', label: 'Semanal (un día)' },
  { value: 'biweekly', label: 'Quincenal' },
  { value: 'monthly', label: 'Mensual' },
];

export const DAYS_OF_WEEK: { value: DayOfWeek; label: string; short: string }[] = [
  { value: 'MON', label: 'Lunes', short: 'L' },
  { value: 'TUE', label: 'Martes', short: 'M' },
  { value: 'WED', label: 'Miércoles', short: 'X' },
  { value: 'THU', label: 'Jueves', short: 'J' },
  { value: 'FRI', label: 'Viernes', short: 'V' },
  { value: 'SAT', label: 'Sábado', short: 'S' },
  { value: 'SUN', label: 'Domingo', short: 'D' },
];

// ============================================================================
// FILL LEVEL THRESHOLDS
// ============================================================================

export const FILL_LEVEL_THRESHOLDS = {
  low: 30, // 0-30% = bajo
  medium: 70, // 31-70% = medio
  high: 90, // 71-90% = alto
  critical: 100, // 91-100% = crítico
};

export const FILL_LEVEL_COLORS = {
  low: '#22c55e', // verde
  medium: '#f59e0b', // amarillo
  high: '#f97316', // naranja
  critical: '#ef4444', // rojo
};

export function getFillLevelColor(fillLevel: number): string {
  if (fillLevel <= FILL_LEVEL_THRESHOLDS.low) return FILL_LEVEL_COLORS.low;
  if (fillLevel <= FILL_LEVEL_THRESHOLDS.medium) return FILL_LEVEL_COLORS.medium;
  if (fillLevel <= FILL_LEVEL_THRESHOLDS.high) return FILL_LEVEL_COLORS.high;
  return FILL_LEVEL_COLORS.critical;
}

export function getFillLevelLabel(fillLevel: number): string {
  if (fillLevel <= FILL_LEVEL_THRESHOLDS.low) return 'Bajo';
  if (fillLevel <= FILL_LEVEL_THRESHOLDS.medium) return 'Medio';
  if (fillLevel <= FILL_LEVEL_THRESHOLDS.high) return 'Alto';
  return 'Crítico';
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

export const DEFAULT_VEHICLE_CAPACITY = 10; // m³
export const DEFAULT_CONTAINER_CAPACITY = 1100; // litros
// DEFAULT_ROUTE_START_TIME moved to route-constants.ts

// ============================================================================
// DRIVER CONSTANTS
// ============================================================================

export const DRIVER_STATUSES: { value: DriverStatus; label: string; color: string }[] = [
  { value: 'active', label: 'Activo', color: '#22c55e' },
  { value: 'inactive', label: 'Inactivo', color: '#6b7280' },
  { value: 'on_leave', label: 'De licencia', color: '#f59e0b' },
  { value: 'suspended', label: 'Suspendido', color: '#ef4444' },
];

export const LICENSE_TYPES: { value: LicenseType; label: string }[] = [
  { value: 'A', label: 'Clase A - Motocicletas' },
  { value: 'B', label: 'Clase B - Automóviles' },
  { value: 'C', label: 'Clase C - Camiones' },
  { value: 'D', label: 'Clase D - Transporte de pasajeros' },
  { value: 'E', label: 'Clase E - Vehículos especiales' },
];

// ============================================================================
// CREW CONSTANTS
// ============================================================================

export const CREW_STATUSES: { value: CrewStatus; label: string; color: string }[] = [
  { value: 'active', label: 'Activa', color: '#22c55e' },
  { value: 'on_break', label: 'En descanso', color: '#3b82f6' },
  { value: 'off_duty', label: 'Fuera de turno', color: '#6b7280' },
  { value: 'incomplete', label: 'Incompleta', color: '#f59e0b' },
];

export const CREW_SHIFTS: { value: CrewShift; label: string }[] = [
  { value: 'morning', label: 'Mañana (6:00 - 14:00)' },
  { value: 'afternoon', label: 'Tarde (14:00 - 22:00)' },
  { value: 'night', label: 'Noche (22:00 - 6:00)' },
  { value: 'rotating', label: 'Rotativo' },
];

// ============================================================================
// ELEMENT COLORS (per tab/element type — user-editable)
// ============================================================================

export { WASTE_ELEMENT_COLORS, MAPPABLE_ELEMENT_IDS, type WasteElementId } from './element-colors';

// ============================================================================
// ROUTE CONSTANTS
// ============================================================================

export {
  ROUTE_STATUSES,
  ROUTE_COLORS,
  getRouteColor,
  getRouteStatusLabel,
  DEFAULT_ROUTE_PROPS,
  DEFAULT_ROUTE_START_TIME,
  DEFAULT_FREQUENCY,
  DEFAULT_OPERATING_DAYS,
  getOperatingDaysForFrequency,
  getFrequencyColor,
  ROUTE_CODE_PREFIX,
  generateRouteCode,
  ROUTE_LINE_STYLES,
  ROUTE_ARROW_STYLES,
  ROUTE_LABEL_STYLES,
  ROUTE_LAYER_IDS,
  ROUTE_OPTIMIZATION_CONFIG,
  ROUTE_VALIDATION,
  canOptimizeRoute,
  isValidRouteCode,
  isValidRouteName,
  ROUTE_MESSAGES,
} from './route-constants';
